import React, { useCallback, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import { api } from '../utils/apiHelper';
import UserContext from '../context/UserContext';
import BeatBattleContext from '../context/BeatBattleContext';
import '../styles/BeatBattle.css';

const BeatBattle = () => {
  const { authUser } = useContext(UserContext);
  const navigate = useNavigate();
  const {
    beatBattleData,
    setBeatBattleData,
    clearBeatBattleData
  } = useContext(BeatBattleContext);

  const [audioFiles, setAudioFiles] = useState([]);
  const [coverArt, setCoverArt] = useState(null);
  const [projectTitle, setProjectTitle] = useState('');
  const [prodName, setProdName] = useState('');
  const [location, setLocation] = useState('');
  const [social, setSocial] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [titleError, setTitleError] = useState('');
  const [prodError, setProdError] = useState('');
  const projectId = uuidv4();

  // Restore form from context if exists
  useEffect(() => {
    if (beatBattleData) {
      setProjectTitle(beatBattleData.projectTitle || '');
      setProdName(beatBattleData.prodName || '');
      setLocation(beatBattleData.location || '');
      setSocial(beatBattleData.social || '');
      setAudioFiles(beatBattleData.audioFiles || []);
      setCoverArt(beatBattleData.coverArt || null);
    }
  }, [beatBattleData]);

  const images = { origin: 'origin.png' };

  const onDropAudio = useCallback((acceptedFiles) => {
    setAudioFiles(prev => [...prev, ...acceptedFiles]);
  }, []);

  const onDropImage = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) setCoverArt(acceptedFiles[0]);
  }, []);

          const {
            getRootProps: getAudioRootProps,
            getInputProps: getAudioInputProps
          } = useDropzone({
            onDrop: onDropAudio,
            accept: 'audio/*',
            multiple: true
          });

          const {
            getRootProps: getImageRootProps,
            getInputProps: getImageInputProps
          } = useDropzone({
            onDrop: onDropImage,
            accept: 'image/*',
            multiple: false
          });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!projectTitle.trim()) return setTitleError('Project title is required');
    if (!prodName.trim()) return setProdError('Producer name is required');
    if (audioFiles.length === 0) return alert("Please upload at least one audio file.");
    if (!coverArt) return alert("Please upload a cover art image.");

    // 🔐 Redirect to signup if not authenticated
    if (!authUser || !authUser._id) {
      setBeatBattleData({
        projectTitle,
        prodName,
        location,
        social,
        audioFiles,
        coverArt,
      });
      return navigate('/signup');
    }

    setUploading(true);
    const uploadedFiles = [];

    const uploadSingleFile = async (file) => {
      const fileId = uuidv4();
      const { signedUrl, key } = await api('/beat-battle-signed-url', 'POST', {
        userId: authUser._id,
        projectId,
        fileName: file.name,
        fileType: file.type,
      });

      await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      return { fileId, key, fileName: file.name, type: file.type };
    };

    try {
      for (let i = 0; i < audioFiles.length; i++) {
        const uploaded = await uploadSingleFile(audioFiles[i]);
        uploadedFiles.push(uploaded);
        setUploadProgress(Math.round(((i + 1) / (audioFiles.length + 1)) * 100));
      }

      const coverUpload = await uploadSingleFile(coverArt);
      uploadedFiles.push({ ...coverUpload, isCoverArt: true });

      await api('/beat-battle-submit', 'POST', {
        userId: authUser._id,
        email: authUser.email,
        projectId,
        projectTitle,
        producerName: prodName,
        location,
        socialContact: social,
        files: uploadedFiles,
      });

      alert("Submission successful!");
      setAudioFiles([]);
      setCoverArt(null);
      setProjectTitle('');
      setProdName('');
      setLocation('');
      setSocial('');
      clearBeatBattleData(); // ✅ Clear stored form after successful submission
    } catch (error) {
      console.error("Upload or submission failed:", error);
      alert("Submission failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="beat-battle-container">
      <div className="theHeaders">
        <h2 className="BeatBattleh2">Enter to Win</h2>
        <h1>"Origins Beat Battle"</h1>
      </div>

      <div className="marquee">
        <img src={images.origin} className="origin" alt="origin" />
      </div>

      <div className="allYall">
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Beat Name"
            value={projectTitle}
            maxLength="20"
            className="popStyle"
            onChange={(e) => {
              setProjectTitle(e.target.value);
              setTitleError('');
            }}
          />
          {titleError && <p className="error">{titleError}</p>}

          <input
            type="text"
            placeholder="Producer Name"
            value={prodName}
            maxLength="20"
            className="popStyle"
            onChange={(e) => {
              setProdName(e.target.value);
              setProdError('');
            }}
          />
          {prodError && <p className="error">{prodError}</p>}

          <input
            type="text"
            placeholder="Location (optional)"
            value={location}
            className="popStyle"
            maxLength="30"
            onChange={(e) => setLocation(e.target.value)}
          />

          <input
            type="text"
            placeholder="Short Message (optional)"
            value={social}
            className="popStyle"
            maxLength="180" 
            onChange={(e) => setSocial(e.target.value)}
          />

          <div {...getImageRootProps({ className: 'battle-dropzone' })}>
            <input {...getImageInputProps()} />
            <p>Upload Cover Art</p>
          </div>

          <div {...getAudioRootProps({ className: 'battle-dropzone' })}>
            <input {...getAudioInputProps()} />
            <p>Upload Audio File</p>
          </div>

          {coverArt && <p><strong>Selected Cover Art:</strong> {coverArt.name}</p>}

          {audioFiles.length > 0 && (
            <ul>
              {audioFiles.map((file, idx) => (
                <li key={idx}>{file.name}</li>
              ))}
            </ul>
          )}

          <button type="submit" className="dUploadButton" disabled={uploading}>
            {uploading ? `Uploading... (${uploadProgress}%)` : 'Submit Entry'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BeatBattle;
